/**
 * SB Pricing Orchestrator
 *
 * Single source of truth for the price + compare-at price shown on the
 * Add to Bag (ATC) button. The form is rendered with
 * data-sb-pricing-active="true" (see buy-buttons.liquid) so the legacy
 * inline writer in add-to-cart-button.liquid stands down and only this
 * module writes the ATC price wrap.
 *
 * Covers all 6 cases from the product page spec:
 *   1. Normal product, variant change
 *   2. Subscription, Subscribe & Save selected
 *   3. Subscription, One-Time Purchase selected
 *   4. Bundle 1-bottle tier, Subscribe & Save
 *   5. Bundle 1-bottle tier, One-Time
 *   6. Bundle 2/3-bottle tier — subscription forced off, bundle total shown
 */
(function () {
  'use strict';

  function init() {
    var productDataNode = document.getElementById('sb-product-pricing-data');
    if (!productDataNode) return;

    // The form is rendered with data-sb-pricing-active="true" by Liquid
    // (see buy-buttons.liquid), which is what tells the legacy ATC writer
    // in add-to-cart-button.liquid to stand down.

    var productData;
    try {
      productData = JSON.parse(productDataNode.textContent);
    } catch (e) {
      return;
    }

    var bundleDataNode = document.getElementById('sb-bundle-pricing-data');
    var bundleData = null;
    if (bundleDataNode) {
      try {
        bundleData = JSON.parse(bundleDataNode.textContent);
      } catch (e) {
        bundleData = null;
      }
    }

    var form = productDataNode.closest('form');
    if (!form) return;

    var variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) return;

    var blockId = productDataNode.getAttribute('data-block-id');
    var bundleSelector = blockId
      ? document.querySelector('.bundle-quantity-selector[data-block-id="' + blockId + '"]')
      : null;
    var sealTarget = form.querySelector('.sealsubs-target-element');

    var state = {
      variantId: parseInt(variantInput.value, 10) || productData.defaultVariantId,
      bundleTier: bundleData ? bundleData.defaultHandle : null,
      purchaseType: 'subscribe'
    };

    function findVariant(id) {
      var source = productData.variants;
      if (state.bundleTier && bundleData) {
        var tier = bundleData.tiers.find(function (t) { return t.handle === state.bundleTier; });
        if (tier && tier.variants && tier.variants.length) {
          source = tier.variants;
        }
      }
      return source.find(function (v) { return v.id === id; }) || source[0];
    }

    function resolvePrice() {
      var variant = findVariant(state.variantId);
      if (!variant) return { price: 0, compareAt: 0 };

      var isBundleTier = false;
      var tier = null;
      if (bundleData && state.bundleTier) {
        tier = bundleData.tiers.find(function (t) { return t.handle === state.bundleTier; });
        isBundleTier = !!tier;
      }

      if (isBundleTier && tier && !tier.subscriptionAvailable) {
        return { price: variant.price, compareAt: variant.compareAt, source: 'bundle-onetime' };
      }

      var allocations = variant.sellingPlanAllocations || [];
      var wantsSubscribe = state.purchaseType === 'subscribe';
      var hasSellingPlans = productData.hasSellingPlans || allocations.length > 0;

      if (wantsSubscribe && hasSellingPlans && allocations.length > 0) {
        var spa = allocations.reduce(function (cheapest, a) {
          return !cheapest || a.price < cheapest.price ? a : cheapest;
        }, null);
        if (spa) {
          return {
            price: spa.price,
            compareAt: spa.compareAt > spa.price ? spa.compareAt : variant.compareAt,
            source: isBundleTier ? 'bundle-subscribe' : 'subs-subscribe'
          };
        }
      }

      return { price: variant.price, compareAt: variant.compareAt, source: isBundleTier ? 'bundle-onetime' : 'normal' };
    }

    function formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    function roundUpForAtc(moneyString) {
      if (!moneyString) return moneyString;
      var match = moneyString.match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/);
      if (!match) return moneyString;
      var prefix = match[1];
      var num = parseFloat(match[2]);
      var suffix = match[3];
      if (isNaN(num)) return moneyString;
      var rounded = Math.round(num);
      return prefix + String(rounded) + suffix;
    }

    function writeAtcButton() {
      var resolved = resolvePrice();
      var labelEl = form.querySelector('.add-to-cart-text__content') ||
        form.querySelector('.add-to-cart-text__label');
      if (!labelEl) return;

      var wrap = labelEl.querySelector('.custom-atc-price-wrap');
      if (!wrap) {
        wrap = document.createElement('span');
        wrap.className = 'custom-atc-price-wrap';
        wrap.style.cssText = 'display:inline-flex;align-items:center;gap:4px;margin-left:6px;';
        wrap.innerHTML =
          '<span class="custom-atc-strike-price" style="text-decoration:line-through;margin-right:4px;display:none;"></span>' +
          '<span class="atc-price"></span>';
        labelEl.appendChild(document.createTextNode(' '));
        labelEl.appendChild(wrap);
      }

      var priceEl = wrap.querySelector('.atc-price');
      var strikeEl = wrap.querySelector('.custom-atc-strike-price');
      if (priceEl) priceEl.textContent = roundUpForAtc(formatMoney(resolved.price));
      if (strikeEl) {
        if (resolved.compareAt && resolved.compareAt > resolved.price) {
          strikeEl.textContent = roundUpForAtc(formatMoney(resolved.compareAt));
          strikeEl.style.display = '';
        } else {
          strikeEl.textContent = '';
          strikeEl.style.display = 'none';
        }
      }
    }

    function readSealPurchaseType() {
      if (!sealTarget) return null;
      var active = sealTarget.querySelector('.sls-option-container.sls-active');
      if (!active) {
        active = sealTarget.querySelector('.sls-option-container');
      }
      if (!active) return null;
      var isSubscribe =
        active.hasAttribute('data-selling-plan-group') ||
        (active.innerText || '').toLowerCase().indexOf('subscribe') !== -1;
      return isSubscribe ? 'subscribe' : 'one_time';
    }

    function render() {
      var pt = readSealPurchaseType();
      if (pt) state.purchaseType = pt;
      writeAtcButton();
    }

    function variantIdFromEvent(e) {
      var detail = e.detail || {};
      var resource = detail.resource || detail.variant || null;
      if (resource && (resource.id || resource.id === 0)) {
        return parseInt(resource.id, 10);
      }
      var formVariantInput = form.querySelector('input[name="id"]');
      if (formVariantInput && formVariantInput.value) {
        return parseInt(formVariantInput.value, 10);
      }
      return null;
    }

    document.addEventListener('variant:update', function (e) {
      var id = variantIdFromEvent(e);
      if (id) state.variantId = id;
      render();
    });

    document.addEventListener('variant:selected', function (e) {
      var id = variantIdFromEvent(e);
      if (id) state.variantId = id;
      render();
    });

    if (bundleSelector) {
      bundleSelector.addEventListener('change', function (e) {
        if (e.target && e.target.classList && e.target.classList.contains('bundle-option__radio')) {
          state.bundleTier = e.target.value;
          var dataset = e.target.dataset || {};
          if (dataset.variantId) {
            state.variantId = parseInt(dataset.variantId, 10);
            if (variantInput) variantInput.value = dataset.variantId;
          }
          var tier = bundleData.tiers.find(function (t) { return t.handle === state.bundleTier; });
          if (tier && !tier.subscriptionAvailable) {
            state.purchaseType = 'one_time';
            if (sealTarget) {
              sealTarget.style.display = 'none';
              sealTarget.querySelectorAll('input, select').forEach(function (el) {
                el.disabled = true;
              });
            }
          } else {
            if (sealTarget) {
              sealTarget.style.display = '';
              sealTarget.querySelectorAll('input, select').forEach(function (el) {
                el.disabled = false;
              });
            }
          }
          render();
        }
      });
    }

    if (sealTarget) {
      var sealDebounce = null;
      var sealHandler = function () {
        if (sealDebounce) clearTimeout(sealDebounce);
        sealDebounce = setTimeout(render, 80);
      };
      sealTarget.addEventListener('click', sealHandler);
      sealTarget.addEventListener('change', sealHandler);
    }

    var tries = 0;
    var interval = setInterval(function () {
      tries++;
      if (sealTarget && sealTarget.querySelector('.sls-option-container')) {
        render();
        clearInterval(interval);
      } else if (tries > 40) {
        render();
        clearInterval(interval);
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
